import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPrint } from 'react-icons/fa';
import { fetchSellerPaymentInvoiceDetail } from '../../services/infrastructureLeasingListing.service';
import { resolvePublicFileUrl } from '../../constants/api-urls';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_leasing_buyer_invoice.scss';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function PartyCard({ heading, party }) {
  if (!party) {
    return (
      <div className="ile-invoice-document__party-box">
        <div className="ile-invoice-document__party-heading">{heading}</div>
        <div className="text-muted">—</div>
      </div>
    );
  }
  const d = party.displayName != null ? String(party.displayName).trim() : '';
  const c = party.companyName != null ? String(party.companyName).trim() : '';
  const primary = d || c || '—';
  const secondary = c && d && c !== d ? c : null;
  return (
    <div className="ile-invoice-document__party-box">
      <div className="ile-invoice-document__party-heading">{heading}</div>
      <div className="ile-invoice-document__party-name">{primary}</div>
      {secondary ? <div className="text-secondary">{secondary}</div> : null}
      {party?.email ? (
        <div>
          <span className="text-muted">Email:</span> {party.email}
        </div>
      ) : null}
      {party?.phone ? (
        <div>
          <span className="text-muted">Phone:</span> {party.phone}
        </div>
      ) : null}
      {party?.addressLine ? (
        <div className="mt-1 text-break">
          <span className="text-muted">Address:</span> {party.addressLine}
        </div>
      ) : null}
    </div>
  );
}

export default function InfrastructureLeasingSellerPaymentInvoicePage() {
  const { listingId: listingParam, enquiryId: enquiryParam } = useParams();
  const navigate = useNavigate();
  const listingIdNum = useMemo(() => {
    const n = parseInt(String(listingParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [listingParam]);
  const enquiryIdNum = useMemo(() => {
    const n = parseInt(String(enquiryParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [enquiryParam]);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const enquiriesPath = `/infrastructure-leasing/my-properties/${listingIdNum}/enquiries`;

  const load = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchSellerPaymentInvoiceDetail(listingIdNum, enquiryIdNum);
      if (res?.success && res.data) {
        setData(res.data);
      } else {
        setData(null);
        toast.error(res?.message || 'Could not load payment details.');
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message;
      setData(null);
      if (status === 409) {
        toast.warning(msg || 'Payment has not been completed yet.');
        navigate(enquiriesPath, { replace: true });
      } else if (status === 403 || status === 404) {
        toast.error(msg || 'Access denied or not found.');
        navigate('/infrastructure-leasing/my-properties', { replace: true });
      } else {
        toast.error(msg || 'Could not load payment details.');
      }
    } finally {
      setLoading(false);
    }
  }, [listingIdNum, enquiryIdNum, navigate, enquiriesPath]);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePrint = () => {
    window.print();
  };

  const docUrl =
    data?.agreementDocumentPath && String(data.agreementDocumentPath).trim()
      ? resolvePublicFileUrl(data.agreementDocumentPath)
      : '';

  if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
    return (
      <div className="container-fluid py-4">
        <p className="text-muted">Invalid link.</p>
        <Link to="/infrastructure-leasing/my-properties">My properties</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-muted py-5">Loading payment &amp; invoice…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container-fluid py-4">
        <p className="text-muted">Nothing to display.</p>
        <Link to={enquiriesPath}>Back to enquiries</Link>
      </div>
    );
  }

  const pay = data.payment || {};
  const inv = data.invoice || {};
  const bill = data.billing || {};
  const lease = data.lease || {};

  return (
    <div className="container-fluid py-4">
      <div className="mx-auto" style={{ maxWidth: 920 }}>
        <nav aria-label="breadcrumb" className="mb-3 ile-invoice-print-hide">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/dashboard/seller">Seller dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/infrastructure-leasing/my-properties">My properties</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={enquiriesPath}>Enquiries</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Payment &amp; invoice
            </li>
          </ol>
        </nav>

        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3 ile-invoice-print-hide">
          <div>
            <h1 className="h4 mb-1">Payment &amp; invoice</h1>
            <p className="text-muted small mb-0">
              {data.listingTitle ? (
                <>
                  <span className="fw-semibold text-body">{data.listingTitle}</span>
                  {data.agreementReference ? (
                    <span className="text-muted"> · {data.agreementReference}</span>
                  ) : null}
                </>
              ) : (
                `Listing #${listingIdNum}`
              )}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
            onClick={handlePrint}
          >
            <FaPrint aria-hidden /> Print / PDF
          </button>
        </div>

        <div className="ile-invoice-document mb-4" id="ile-seller-payment-print-root">
          <div className="ile-invoice-document__banner">
            <h2 className="ile-invoice-document__banner-title mb-0">Buyer payment record</h2>
            <div className="ile-invoice-document__banner-meta text-end">
              <div>Infrastructure leasing · Seller view</div>
              <div className="small mt-1 opacity-75">Enquiry #{data.enquiryId}</div>
            </div>
          </div>

          <div className="ile-invoice-document__body">
            <h3 className="h6 fw-semibold mb-3">Payment details</h3>
            <div className="ile-invoice-document__meta-grid mb-4">
              <div>
                <span className="ile-invoice-document__meta-label">Amount paid</span>
                <div>{pay.amountLabel || '—'}</div>
              </div>
              <div>
                <span className="ile-invoice-document__meta-label">Paid on</span>
                <div>{formatWhen(pay.paidAt)}</div>
              </div>
              <div>
                <span className="ile-invoice-document__meta-label">Method</span>
                <div>{pay.methodLabel || '—'}</div>
              </div>
              <div>
                <span className="ile-invoice-document__meta-label">Status</span>
                <div>
                  <span className="badge text-bg-success text-capitalize">{pay.status || 'success'}</span>
                </div>
              </div>
              <div>
                <span className="ile-invoice-document__meta-label">Transaction / invoice ref.</span>
                <div className="font-monospace small">{pay.transactionReference || '—'}</div>
              </div>
            </div>

            <h3 className="h6 fw-semibold mb-3 border-top pt-3">Invoice details</h3>
            <div className="ile-invoice-document__meta-grid mb-4">
              <div>
                <span className="ile-invoice-document__meta-label">Invoice ID</span>
                <div className="font-monospace fw-semibold">{inv.reference || '—'}</div>
              </div>
              <div>
                <span className="ile-invoice-document__meta-label">Invoice amount</span>
                <div>{inv.amountLabel || '—'}</div>
              </div>
              <div>
                <span className="ile-invoice-document__meta-label">Issued</span>
                <div>{formatWhen(inv.issuedAt)}</div>
              </div>
              <div>
                <span className="ile-invoice-document__meta-label">Lease period</span>
                <div className="small">
                  {lease.leaseStartDate || lease.leaseEndDate ? (
                    <>
                      {lease.leaseStartDate ? new Date(lease.leaseStartDate.slice(0, 10)).toLocaleDateString() : '—'}{' '}
                      →{' '}
                      {lease.leaseEndDate ? new Date(lease.leaseEndDate.slice(0, 10)).toLocaleDateString() : '—'}
                    </>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
            </div>

            <h3 className="h6 fw-semibold mb-3">Billing</h3>
            <div className="ile-invoice-document__parties mb-0">
              <PartyCard heading="Seller (lessor)" party={bill.seller} />
              <PartyCard heading="Buyer (lessee)" party={bill.buyer} />
            </div>

            {docUrl ? (
              <div className="border-top pt-3 mt-3 ile-invoice-print-hide">
                <span className="fw-semibold d-block mb-2">Agreement document</span>
                <a className="btn btn-sm btn-outline-primary me-2" href={docUrl} target="_blank" rel="noopener noreferrer">
                  View PDF
                </a>
                <a className="btn btn-sm btn-outline-secondary" href={docUrl} download>
                  Download
                </a>
              </div>
            ) : null}
          </div>
        </div>

        <p className="small ile-invoice-print-hide">
          <Link to={enquiriesPath} className="text-muted">
            ← Back to enquiries
          </Link>
        </p>
      </div>
    </div>
  );
}
